import { css } from '@emotion/react'
import { Cell, Grid, Heading, HFlow, Text } from 'bold-ui'
import { Divider } from 'components/divider/Divider'
import { HLabel } from 'components/HLabel'
import { format } from 'date-fns'
import { Fragment } from 'react'

import { InformacoesAdministrativas } from '../../../model/model-historico'
import { HistoricoInfoProfissional } from './HistoricoInfoProfissional'
import { ProfissionalListLabel } from './ProfissionalListLabel'

interface HistoricoInfoAdminPanelProps {
  content: InformacoesAdministrativas
}

export default function HistoricoInfoAdminPanel(props: HistoricoInfoAdminPanelProps) {
  const { content } = props

  const principal = content.profissionalPrincipal
  const compartilhado = content.profissionalCompartilhado

  const hasDadosEstagiario = content.nomeCivilEstagiario
  const hasDadosLocalAtendimento =
    principal?.cnesUnidadeSaude || content.localAtendimento || content.localAtividadeColetiva
  const descricaoInepFormatted = content.nomeInep
    ? `${content.nomeInep.titleCase()} - ${content.codigoInep}`
    : content.codigoInep

  return (
    <Fragment>
      <Divider
        style={css`
          margin: 1rem -1rem;
        `}
      />
      <div>
        <Grid gapVertical={0.5} style={styles.grid}>
          <Cell size={12}>
            <Heading level={3}>Informações administrativas</Heading>
          </Cell>
          <HistoricoInfoProfissional
            label={content.stCuidadoCompartilhado && 'Registrado por:'}
            profissional={principal}
          />
          {hasDadosEstagiario && (
            <Cell size={12}>
              <HFlow hSpacing={0}>
                <HLabel title='Estagiário:'>
                  <ProfissionalListLabel
                    nome={content.nomeSocialEstagiario ?? content.nomeCivilEstagiario}
                    nomeSocial={content.nomeSocialEstagiario}
                    nomeCivil={content.nomeCivilEstagiario}
                    cbo={content.cboEstagiario}
                    ineEquipe={content.ineEquipeEstagiario}
                    nomeEquipe={content.nomeEquipeEstagiario}
                  />
                </HLabel>
              </HFlow>
            </Cell>
          )}
          {content.stRegistroTardio && (
            <Cell size={12}>
              <Text fontStyle='italic'>
                {`Registro tardio registrado em ${format(content.dataCriacaoRegistro, "dd/MM/yyyy' às 'HH:mm")}`}
              </Text>
            </Cell>
          )}
          {(content.cpfCnsCuidador || content.tipoCuidador) && (
            <Cell size={12}>
              <HLabel title='Cuidador:'>
                <Text>
                  {`${content.tipoCuidador ?? ''}
                  ${
                    content.tipoCuidador && content.cpfCnsCuidador
                      ? ` - ${content.cpfCnsCuidador ?? ''}`
                      : `${content.cpfCnsCuidador ?? ''}`
                  }`}
                </Text>
              </HLabel>
            </Cell>
          )}
          {hasDadosLocalAtendimento && (
            <Cell size={12}>
              <HFlow hSpacing={0} style={styles.separator}>
                {principal?.cnesUnidadeSaude && (
                  <HLabel title='CNES:'>
                    <Text>{`${principal.nomeUnidadeSaude} - ${principal.cnesUnidadeSaude}`}</Text>
                  </HLabel>
                )}
                {content.localAtendimento && (
                  <HLabel title='Local de atendimento:'>
                    <Text>{content.localAtendimento}</Text>
                  </HLabel>
                )}
                {(content.codigoInep || content.localAtividadeColetiva) && (
                  <HLabel title='Local de atividade:'>
                    {content.codigoInep && <Text>{descricaoInepFormatted}</Text>}
                    {content.localAtividadeColetiva && <Text>{content.localAtividadeColetiva}</Text>}
                  </HLabel>
                )}
              </HFlow>
            </Cell>
          )}
          {content.stAtendimentoCompartilhado && (
            <Cell size={12}>
              <div css={styles.separator}>
                {compartilhado.nome && (
                  <HLabel title='Atendimento compartilhado com:' style={styles.inline}>
                    <ProfissionalListLabel
                      nome={compartilhado.nome}
                      nomeSocial={compartilhado.nomeSocial}
                      nomeCivil={compartilhado.nomeCivil}
                      cbo={compartilhado.cbo}
                      ineEquipe={compartilhado.ineEquipe}
                      nomeEquipe={compartilhado.nomeEquipe}
                    />
                  </HLabel>
                )}
                {content.tipoParticipacaoProfissionalConvidado && (
                  <HLabel title='Forma de participação:' style={styles.inline}>
                    <Text>{content.tipoParticipacaoProfissionalConvidado}</Text>
                  </HLabel>
                )}
              </div>
            </Cell>
          )}
          {content.profissionalFinalizadorObservacao && (
            <HistoricoInfoProfissional
              profissional={content.profissionalFinalizadorObservacao}
              label='Atendimento finalizado por:'
            />
          )}
        </Grid>
      </div>
    </Fragment>
  )
}

const styles = {
  grid: css`
    margin-top: -0.5rem;
    margin-bottom: -0.5rem;
  `,
  separator: css`
    & > :not(:last-child):after {
      content: '|';
      margin-left: 0.25rem;
      margin-right: 0.25rem;
    }
  `,
  inline: css`
    display: inline;
  `,
}
